import React, { useEffect, useState } from 'react';

interface SoundWave3DProps {
  className?: string;
}

interface CameraFlash {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  intensity: number;
  duration: number;
  startTime: number;
  isActive: boolean;
}

const SoundWave3D: React.FC<SoundWave3DProps> = ({ className = '' }) => {
  const [cameraFlashes, setCameraFlashes] = useState<CameraFlash[]>([]);
  const [time, setTime] = useState(0);

  useEffect(() => {
    // Initialize camera flash positions (fewer, more strategic)
    const initialFlashes: CameraFlash[] = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 20 + 15, // Larger, more diffuse
      opacity: 0,
      intensity: Math.random() * 0.8 + 0.4,
      duration: Math.random() * 1000 + 800, // 800-1800ms duration
      startTime: 0,
      isActive: false,
    }));
    setCameraFlashes(initialFlashes);

    // Animate camera flashes and time
    const interval = setInterval(() => {
      setTime(prev => prev + 0.01); // Slower time progression
      
      setCameraFlashes(prevFlashes => 
        prevFlashes.map(flash => {
          const currentTime = Date.now();
          
          // Randomly trigger flashes (much less frequent)
          if (!flash.isActive && Math.random() < 0.003) { // Very low probability
            return {
              ...flash,
              isActive: true,
              startTime: currentTime,
              x: Math.random() * 100, // New random position
              y: Math.random() * 100,
              size: Math.random() * 25 + 20, // Vary size
              duration: Math.random() * 1200 + 1000, // 1-2.2 seconds
              intensity: Math.random() * 0.6 + 0.3,
            };
          }
          
          if (flash.isActive) {
            const elapsed = currentTime - flash.startTime;
            const progress = elapsed / flash.duration;
            
            if (progress >= 1) {
              // Flash completed
              return {
                ...flash,
                isActive: false,
                opacity: 0,
              };
            }
            
            // Camera flash curve: quick rise, slow fade
            let opacity;
            if (progress < 0.1) {
              // Quick flash
              opacity = (progress / 0.1) * flash.intensity;
            } else {
              // Slow fade
              opacity = flash.intensity * (1 - ((progress - 0.1) / 0.9)) * 0.8;
            }
            
            return {
              ...flash,
              opacity: Math.max(0, opacity),
            };
          }
          
          return flash;
        })
      );
    }, 80); // Slower update rate

    return () => clearInterval(interval);
  }, []);

  // Fashion-inspired geometric shapes for light mode
  const FashionShape = ({ delay = 0 }: { delay?: number }) => (
    <div 
      className="absolute w-16 h-16 opacity-20"
      style={{
        left: '50%',
        top: '50%',
        transform: `translate(-50%, -50%) rotate(${time * 20 + delay}deg)`,
      }}
    >
      <div className="w-full h-full border border-pink-300/40 rotate-45" />
      <div className="absolute inset-2 border border-purple-300/30 rotate-12" />
    </div>
  );

  // Fashion-inspired geometric shapes for dark mode
  const FashionShapeDark = ({ delay = 0 }: { delay?: number }) => (
    <div 
      className="absolute w-16 h-16 opacity-30"
      style={{
        left: '50%',
        top: '50%',
        transform: `translate(-50%, -50%) rotate(${time * 20 + delay}deg)`,
      }}
    >
      <div className="w-full h-full border border-purple-400/50 rotate-45" />
      <div className="absolute inset-2 border border-indigo-400/40 rotate-12" />
    </div>
  );

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Flowing fabric-like waves */}
      <div className="absolute inset-0">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="none">
          <defs>
            {/* Light mode gradients */}
            <linearGradient id="fashionWave1Light" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(251, 207, 232)" stopOpacity="0.3" />
              <stop offset="50%" stopColor="rgb(233, 213, 255)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="rgb(219, 234, 254)" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="fashionWave2Light" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(244, 114, 182)" stopOpacity="0.2" />
              <stop offset="50%" stopColor="rgb(196, 181, 253)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="rgb(147, 197, 253)" stopOpacity="0.2" />
            </linearGradient>
            
            {/* Dark mode gradients */}
            <linearGradient id="fashionWave1Dark" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(147, 51, 234)" stopOpacity="0.4" />
              <stop offset="50%" stopColor="rgb(168, 85, 247)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="rgb(99, 102, 241)" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="fashionWave2Dark" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(236, 72, 153)" stopOpacity="0.3" />
              <stop offset="50%" stopColor="rgb(139, 92, 246)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="rgb(79, 70, 229)" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          
          {/* Elegant flowing waves - adapt to dark/light mode */}
          <path 
            d={`M0,${400 + Math.sin(time * 0.8) * 30} C300,${350 + Math.sin(time * 0.6) * 40} 600,${450 + Math.sin(time * 0.9) * 35} 900,${400 + Math.sin(time * 0.7) * 30} C1050,${375 + Math.sin(time * 0.5) * 25} 1200,${425 + Math.sin(time * 0.8) * 30} 1200,${400 + Math.sin(time * 0.8) * 30} L1200,800 L0,800 Z`}
            fill="url(#fashionWave1Light)"
            className="dark:hidden"
          />
          <path 
            d={`M0,${400 + Math.sin(time * 0.8) * 30} C300,${350 + Math.sin(time * 0.6) * 40} 600,${450 + Math.sin(time * 0.9) * 35} 900,${400 + Math.sin(time * 0.7) * 30} C1050,${375 + Math.sin(time * 0.5) * 25} 1200,${425 + Math.sin(time * 0.8) * 30} 1200,${400 + Math.sin(time * 0.8) * 30} L1200,800 L0,800 Z`}
            fill="url(#fashionWave1Dark)"
            className="hidden dark:block"
          />
          
          <path 
            d={`M0,${500 + Math.sin(time * 0.6 + 1) * 25} C400,${450 + Math.sin(time * 0.8 + 1) * 30} 800,${550 + Math.sin(time * 0.7 + 1) * 35} 1200,${500 + Math.sin(time * 0.9 + 1) * 25} L1200,800 L0,800 Z`}
            fill="url(#fashionWave2Light)"
            className="dark:hidden"
          />
          <path 
            d={`M0,${500 + Math.sin(time * 0.6 + 1) * 25} C400,${450 + Math.sin(time * 0.8 + 1) * 30} 800,${550 + Math.sin(time * 0.7 + 1) * 35} 1200,${500 + Math.sin(time * 0.9 + 1) * 25} L1200,800 L0,800 Z`}
            fill="url(#fashionWave2Dark)"
            className="hidden dark:block"
          />
        </svg>
      </div>

      {/* Elegant camera flash effects (fashion runway style) */}
      {cameraFlashes.map(flash => (
        flash.isActive && flash.opacity > 0 && (
          <div key={flash.id}>
            {/* Light mode camera flash */}
            <div
              className="absolute rounded-full dark:hidden pointer-events-none"
              style={{
                left: `${flash.x}%`,
                top: `${flash.y}%`,
                width: `${flash.size}px`,
                height: `${flash.size}px`,
                transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, rgba(255, 255, 255, ${flash.opacity}) 0%, rgba(244, 114, 182, ${flash.opacity * 0.6}) 30%, rgba(233, 213, 255, ${flash.opacity * 0.3}) 60%, transparent 100%)`,
                filter: `blur(${flash.size * 0.1}px)`,
                boxShadow: `0 0 ${flash.size * 1.5}px rgba(255, 255, 255, ${flash.opacity * 0.8}), 0 0 ${flash.size * 0.8}px rgba(244, 114, 182, ${flash.opacity * 0.4})`,
              }}
            />
            {/* Dark mode camera flash */}
            <div
              className="absolute rounded-full hidden dark:block pointer-events-none"
              style={{
                left: `${flash.x}%`,
                top: `${flash.y}%`,
                width: `${flash.size}px`,
                height: `${flash.size}px`,
                transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, rgba(255, 255, 255, ${flash.opacity * 0.9}) 0%, rgba(147, 51, 234, ${flash.opacity * 0.7}) 30%, rgba(99, 102, 241, ${flash.opacity * 0.4}) 60%, transparent 100%)`,
                filter: `blur(${flash.size * 0.1}px)`,
                boxShadow: `0 0 ${flash.size * 1.5}px rgba(255, 255, 255, ${flash.opacity * 0.9}), 0 0 ${flash.size * 0.8}px rgba(147, 51, 234, ${flash.opacity * 0.5})`,
              }}
            />
          </div>
        )
      ))}

      {/* Fashion-inspired geometric elements */}
      <div className="dark:hidden">
        <FashionShape delay={0} />
        <FashionShape delay={120} />
        <FashionShape delay={240} />
      </div>
      <div className="hidden dark:block">
        <FashionShapeDark delay={0} />
        <FashionShapeDark delay={120} />
        <FashionShapeDark delay={240} />
      </div>

      {/* Subtle gradient overlays for depth */}
      <div className="absolute top-0 left-0 w-full h-full">
        {/* Light mode overlays */}
        <div className="dark:hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-pink-200/10 via-transparent to-transparent rounded-full" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-purple-200/10 via-transparent to-transparent rounded-full" />
        </div>
        {/* Dark mode overlays */}
        <div className="hidden dark:block">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-purple-500/15 via-transparent to-transparent rounded-full" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-indigo-500/15 via-transparent to-transparent rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default SoundWave3D;
